import React, { useState, useEffect, useMemo } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import styled from 'styled-components'
import { Form } from 'react-bootstrap'
import { useFetch, usePost, usePut, notify } from '../component-items/helpers'
import { usePermissions } from '../../hooks'
import { globalState } from '../../store'

// Components
import CardLoading from '../component-items/loading-popover'
import { Pages } from '../component-items/pager'
import GlobalStyles from '../component-items/styles'
import { TableCell, THSort } from '../component-items/datatable'
import { SearchBox } from '../component-items/search'
import { TableRow } from '../../pages/company/styles'
import { LargeButton } from '../component-items/buttons'
import { RoleBadge, UserType } from './RoleBadge'
import { EditUserModal, UserData } from './EditUserModal'
import { AuditLogTab } from './AuditLogTab'
import { RoleManagementTab } from './RoleManagementTab'
import { PermissionDebuggerTab } from './PermissionDebuggerTab'
import { PermissionMatrixTab } from './PermissionMatrixTab'
import { AdvancedSelect } from '../component-items/advanced-select'
import { PermissionGate } from '../rbac'

// Fontawesome
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faEdit,
  faCheckDouble,
  faTimes,
  faPaperPlane,
  faShieldAlt,
  faUsers,
  faClipboardList,
  faUserShield,
  faUserSecret,
  faBug,
  faTable,
} from '@fortawesome/pro-duotone-svg-icons'

// Format date as "Jan 1, 2025"
const formatFriendlyDate = (dateString: string | null) => {
  if (!dateString) return null
  const date = new Date(dateString)
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

// Tab navigation styles
const TabContainer = styled.div`
  display: flex;
  gap: 0;
  border-bottom: 1px solid #dee2e6;
  margin: 0 1em 1rem 1em;
`

const Tab = styled.button<{ $active: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.25rem;
  background: none;
  border: none;
  border-bottom: 2px solid ${(props) => (props.$active ? '#0d6efd' : 'transparent')};
  color: ${(props) => (props.$active ? '#0d6efd' : '#6c757d')};
  font-weight: ${(props) => (props.$active ? '500' : '400')};
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    color: #0d6efd;
    background: #f8f9fa;
  }
`

type TabType = 'users' | 'roles' | 'matrix' | 'audit-log' | 'debugger'

interface UserListPageProps {
  userType: UserType
  /** Title to display (e.g., "Company Users" or "Warehouse Users") */
  title: string
  /** For warehouse users */
  warehouseId?: number
  /** For merchant users */
  companyId?: number
  /** CSRF token */
  csrf: string
  /** Base path for permissions navigation (e.g., "/company/users" or "/warehouse/users") */
  permissionsBasePath: string
  /** Available warehouses for filtering (warehouse users only) */
  warehouses?: Array<{ id: number; name: string }>
  /** Available roles for filtering */
  roles?: Array<{ id: number; name: string }>
}

// Filter option type
interface FilterOption {
  label: string
  type: 'boolean' | 'multi-select' | 'select'
  value: boolean | any[]
  selected: boolean
  options?: Array<{ value: any; label: string }>
}

// Default filter options for user list (base filters only)
const getBaseFilterOptions = (): FilterOption[] => [
  {
    label: 'Active',
    type: 'boolean',
    value: true,
    selected: true, // Default: show only active users
  },
  {
    label: 'Has 2FA',
    type: 'boolean',
    value: true,
    selected: false,
  },
]

export const UserListPage = ({
  userType,
  title,
  warehouseId,
  companyId,
  csrf,
  permissionsBasePath,
  warehouses,
  roles,
}: UserListPageProps) => {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const { hasAccess, isMerchantUser } = usePermissions()
  const {
    state: { canImpersonate, isImpersonating },
    dispatch,
  } = globalState()

  // Check if user can add/manage users (requires settings operator)
  // This controls access to user management features like assigning roles and creating overrides
  const canManageUsers = hasAccess('settings', 'operator')
  // Check if user can view audit logs (requires settings viewer)
  const canViewAuditLog = hasAccess('settings', 'viewer')
  // Matrix is view-only and available to settings viewers
  const canViewMatrix = hasAccess('settings', 'viewer')

  // Get active tab from URL or default to 'users'
  const activeTab = (searchParams.get('tab') as TabType) || 'users'
  const setActiveTab = (tab: TabType) => {
    const nextParams = new URLSearchParams(searchParams)

    if (tab === 'users') {
      nextParams.delete('tab')
    } else {
      nextParams.set('tab', tab)
    }

    if (tab !== 'matrix') {
      nextParams.delete('matrixSegment')
    }

    setSearchParams(nextParams)
  }

  const openMatrixForSegment = (segment: string) => {
    const nextParams = new URLSearchParams(searchParams)
    nextParams.set('tab', 'matrix')
    nextParams.set('matrixSegment', segment)
    setSearchParams(nextParams)
  }

  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [sort, setSort] = useState<Record<string, string>>({})
  const [open, setOpen] = useState(false)
  const [selectedUser, setSelectedUser] = useState<Partial<UserData>>({})
  const [totalPages, setTotalPages] = useState(1)
  const [users, setUsers] = useState<any[]>([])
  const [disabledResendIds, setDisabledResendIds] = useState<Set<string | number>>(new Set())

  // Filter options state
  const [filterOptions, setFilterOptions] = useState<FilterOption[]>(getBaseFilterOptions)
  const [rolesFilterAdded, setRolesFilterAdded] = useState(false)
  const [warehousesFilterAdded, setWarehousesFilterAdded] = useState(false)

  // Add role filter when roles become available
  useEffect(() => {
    if (rolesFilterAdded) return
    if (!roles || roles.length === 0) return

    setFilterOptions((prev) => {
      if (prev.find((o) => o.label === 'Role')) return prev
      return [
        ...prev,
        {
          label: 'Role',
          type: 'multi-select',
          value: [],
          selected: false,
          options: roles.map((r) => ({ value: r.name, label: r.name })),
        },
      ]
    })
    setRolesFilterAdded(true)
  }, [roles, rolesFilterAdded])

  // Add warehouse filter when multiple warehouses available
  useEffect(() => {
    if (warehousesFilterAdded) return
    if (!warehouses || warehouses.length <= 1) return

    setFilterOptions((prev) => {
      if (prev.find((o) => o.label === 'Warehouse')) return prev
      return [
        ...prev,
        {
          label: 'Warehouse',
          type: 'multi-select',
          value: [],
          selected: false,
          options: warehouses.map((w) => ({ value: w.id, label: w.name })),
        },
      ]
    })
    setWarehousesFilterAdded(true)
  }, [warehouses, warehousesFilterAdded])

  // Build sort query params - default sort by is_active desc (active users first)
  const sortKey = Object.keys(sort)[0] || 'is_active'
  const sortDir = Object.values(sort)[0] || 'desc'
  const sortParams = `&order_by=${sortKey}&sort=${sortDir}`

  // Build filter query params from filterOptions
  const filterParams = useMemo(() => {
    const params: string[] = []
    filterOptions
      .filter((opt: any) => opt.selected)
      .forEach((opt: any) => {
        const key = opt.label.toLowerCase().replace(' ', '_')
        if (opt.type === 'boolean') {
          params.push(`${key}=${opt.value}`)
        } else if (opt.type === 'multi-select' && opt.value.length > 0) {
          params.push(`${key}=${opt.value.map((v: any) => v.value).join(',')}`)
        }
      })
    return params.length > 0 ? `&${params.join('&')}` : ''
  }, [filterOptions])

  // Build API URL based on user type
  const apiUrl =
    userType === 'warehouse'
      ? `/api/warehouse/${warehouseId}/users/?page=${page}&q=${encodeURIComponent(search)}${sortParams}${filterParams}`
      : `/api/company/${companyId}/user/?page=${page}&q=${encodeURIComponent(search)}${sortParams}${filterParams}`

  const { response: resp, loaded, error, placeholder }: any = useFetch(apiUrl, {})
  const { count: total, results }: any = loaded ? resp : {}

  // Page size from backend (typically 25)
  const PAGE_SIZE = 25

  useEffect(() => {
    if (loaded && results) {
      // Backend now filters out warehouse staff for merchant users
      setUsers(results)
      // Calculate total pages based on count from backend
      if (total > 0) {
        setTotalPages(Math.ceil(total / PAGE_SIZE))
      } else {
        setTotalPages(1)
      }
    }
  }, [loaded, results, userType, total])

  // Build toggle active URL based on user type
  const getToggleActiveUrl = (userId: string | number) => {
    return userType === 'warehouse'
      ? `/api/warehouse/${warehouseId}/users/${userId}/toggle_active/`
      : `/api/company/${companyId}/user/${userId}/toggle_active/`
  }

  const updateUser = (userId: string | number) => () => {
    const user = users.find((u) => u.id === userId)
    const newStatus = user?.is_active ? 'deactivate' : 'activate'
    const userName = user ? `${user.first_name} ${user.last_name}`.trim() || user.email : 'this user'

    notify({
      type: 'warning',
      message: `Are you sure you want to ${newStatus} ${userName}?`,
      requestConfirmation: true,
      onSubmit: () => {
        // Optimistically update UI
        setUsers((prev) => prev.map((u) => (u.id === userId ? { ...u, is_active: !u.is_active } : u)))
        usePut(getToggleActiveUrl(userId), {}, csrf)
      },
      autoClose: false,
    })
  }

  // Build resend email URL based on user type
  const getResendEmailUrl = (userId: string | number) => {
    return userType === 'warehouse'
      ? `/api/warehouse/${warehouseId}/users/${userId}/resend-signup-email/`
      : `/api/company/${companyId}/user/${userId}/resend-signup-email/`
  }

  const resendSignupEmail = async (userId: string | number) => {
    const user = users.find((u) => u.id === userId)
    const userName = user ? `${user.first_name} ${user.last_name}`.trim() || user.email : 'this user'

    notify({
      type: 'warning',
      message: `Are you sure you want to resend the signup email to ${userName}?`,
      requestConfirmation: true,
      onSubmit: async () => {
        setDisabledResendIds((prev) => new Set(prev).add(userId))
        const resp: any = await usePost(getResendEmailUrl(userId), {}, csrf, false, true, true)

        if (resp?.success) {
          notify({ title: 'Success!', message: 'Signup email resent', type: 'success' })
        } else {
          notify({ title: 'Error!', message: 'Signup email failed to send', type: 'error' })
        }
      },
      autoClose: false,
    })
  }

  // Impersonate a user (executives only)
  const impersonateUser = async (userId: string | number) => {
    const user = users.find((u) => u.id === userId)
    const userName = user ? `${user.first_name} ${user.last_name}`.trim() || user.email : 'this user'

    notify({
      type: 'warning',
      message: `You are about to view the application as ${userName}. You will be in read-only mode and cannot take any actions.`,
      requestConfirmation: true,
      onSubmit: async () => {
        try {
          const response = await fetch('/api/rbac/impersonate/acquire/', {
            method: 'POST',
            credentials: 'include',
            headers: {
              'X-CSRFToken': csrf,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ user_pk: userId }),
          })

          const data = await response.json()

          if (response.ok && data.success) {
            // Update impersonation state from response
            dispatch({
              type: 'setImpersonation',
              payload: {
                is_impersonating: true,
                can_impersonate: false,
                impersonated_user: data.impersonated_user,
                original_user: null, // Will be set on page reload from session
              },
            })
            // Redirect to impersonated user's dashboard
            window.location.href = data.redirect_url || '/company/dashboard/'
          } else {
            notify({
              title: 'Error',
              message: data.error || 'Failed to impersonate user',
              type: 'error',
            })
          }
        } catch (e) {
          notify({
            title: 'Error',
            message: 'Failed to impersonate user',
            type: 'error',
          })
        }
      },
      autoClose: false,
    })
  }

  return (
    <GlobalStyles.FullPageCard>
      <GlobalStyles.CardHeader>
        <GlobalStyles.CardTitle>
          <h3>
            {title}
            {activeTab === 'users' && <small>{total} Total</small>}
          </h3>
        </GlobalStyles.CardTitle>
        <GlobalStyles.CardToolbar>
          {activeTab === 'users' && (
            <>
              <SearchBox
                {...{ search, setSearch, page, setPage }}
                loading={false}
                setLoading={() => undefined}
                setFocus
                updateURL
              />
              <AdvancedSelect
                placeholder="Filter"
                filterOptions={filterOptions}
                setFilterOptions={setFilterOptions}
                width={280}
              />
              {canManageUsers && (
                <GlobalStyles.Button
                  data-pw="add-user-btn"
                  onClick={() => {
                    setSelectedUser({})
                    setOpen(true)
                  }}
                >
                  Add User
                </GlobalStyles.Button>
              )}
            </>
          )}
        </GlobalStyles.CardToolbar>
      </GlobalStyles.CardHeader>

      {/* Tab Navigation */}
      <TabContainer>
        <Tab $active={activeTab === 'users'} onClick={() => setActiveTab('users')}>
          <FontAwesomeIcon icon={faUsers} />
          Users
        </Tab>
        {canManageUsers && (
          <Tab $active={activeTab === 'roles'} onClick={() => setActiveTab('roles')}>
            <FontAwesomeIcon icon={faUserShield} />
            Roles
          </Tab>
        )}
        {canViewMatrix && (
          <Tab $active={activeTab === 'matrix'} onClick={() => setActiveTab('matrix')}>
            <FontAwesomeIcon icon={faTable} />
            Matrix
          </Tab>
        )}
        {canViewAuditLog && (
          <Tab $active={activeTab === 'audit-log'} onClick={() => setActiveTab('audit-log')}>
            <FontAwesomeIcon icon={faClipboardList} />
            Audit Log
          </Tab>
        )}
        {canManageUsers && (
          <Tab $active={activeTab === 'debugger'} onClick={() => setActiveTab('debugger')}>
            <FontAwesomeIcon icon={faBug} />
            Debugger
          </Tab>
        )}
      </TabContainer>

      {/* Tab Content */}
      {activeTab === 'users' ? (
        <div style={{ minHeight: 500, padding: '0 1em' }}>
          <GlobalStyles.DataTableContainer>
            <GlobalStyles.DataTable>
              <thead>
                <tr>
                  <THSort title="Name" sortKey="name" {...{ sort, setSort }} />
                  <THSort title="Email" sortKey="email" {...{ sort, setSort }} />
                  <THSort title="Username" sortKey="username" {...{ sort, setSort }} />
                  <THSort title="Role" sortKey="role" center {...{ sort, setSort }} />
                  <THSort title="Active" sortKey="is_active" center {...{ sort, setSort }} />
                  <th>
                    <span className="text center">2FA</span>
                  </th>
                  <THSort title="Last Login" sortKey="last_login" center {...{ sort, setSort }} />
                  <THSort title="Date Joined" sortKey="date_joined" center {...{ sort, setSort }} />
                  <th>
                    <span className="text center">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {users?.map((u: any, id: number) => (
                  <TableRow key={id} data-pw={`user-row-${u.id}`}>
                    <TableCell input={u.first_name + ' ' + u.last_name} />
                    <TableCell input={u.email} />
                    <TableCell input={u.username} />
                    <TableCell center input={<RoleBadge role={u.role} userType={userType} />} />
                    <TableCell
                      center
                      input={
                        <PermissionGate
                          segment="settings"
                          minLevel="admin"
                          fallback={<span>{u.is_active ? 'Yes' : 'No'}</span>}
                        >
                          <Form.Check
                            type="switch"
                            id={'active-' + u.id}
                            checked={u.is_active}
                            onChange={updateUser(u.id)}
                          />
                        </PermissionGate>
                      }
                    />
                    <TableCell
                      center
                      input={
                        u.has_2fa ? (
                          <FontAwesomeIcon style={{ color: 'green' }} icon={faCheckDouble} />
                        ) : (
                          <FontAwesomeIcon style={{ color: 'red' }} icon={faTimes} />
                        )
                      }
                    />
                    <TableCell center input={formatFriendlyDate(u.last_login)} />
                    <TableCell center input={formatFriendlyDate(u.date_joined)} />
                    <TableCell
                      center
                      input={
                        <div className="dropdown__container" style={{ display: 'flex', justifyContent: 'center' }}>
                          {canManageUsers && (
                            <>
                              <GlobalStyles.Tooltip data-title="Edit user details">
                                <LargeButton
                                  onClick={() => {
                                    setSelectedUser(u)
                                    setOpen(true)
                                  }}
                                >
                                  <FontAwesomeIcon icon={faEdit} />
                                </LargeButton>
                              </GlobalStyles.Tooltip>
                              <GlobalStyles.Tooltip data-title="Manage permissions">
                                <LargeButton onClick={() => navigate(`${permissionsBasePath}/${u.id}/permissions/`)}>
                                  <FontAwesomeIcon icon={faShieldAlt} />
                                </LargeButton>
                              </GlobalStyles.Tooltip>
                              <GlobalStyles.Tooltip data-title="Resend signup email">
                                <LargeButton
                                  onClick={() => resendSignupEmail(u.id)}
                                  disabled={disabledResendIds.has(u.id)}
                                >
                                  <FontAwesomeIcon icon={faPaperPlane} />
                                </LargeButton>
                              </GlobalStyles.Tooltip>
                            </>
                          )}
                          {canImpersonate && !isImpersonating && !isMerchantUser && (
                            <GlobalStyles.Tooltip data-title="View as this user (diagnostic)">
                              <LargeButton onClick={() => impersonateUser(u.id)} data-testid="impersonate-btn">
                                <FontAwesomeIcon icon={faUserSecret} />
                              </LargeButton>
                            </GlobalStyles.Tooltip>
                          )}
                        </div>
                      }
                    />
                  </TableRow>
                ))}
              </tbody>
            </GlobalStyles.DataTable>
            {!loaded ? <CardLoading text={placeholder} error={error} /> : null}
          </GlobalStyles.DataTableContainer>
          <>
            <br />
            <Pages page={page} setPage={(p: number) => setPage(p)} total={totalPages} />
            <br />
          </>
        </div>
      ) : activeTab === 'roles' ? (
        <div>
          <RoleManagementTab
            userType={userType}
            warehouseId={warehouseId}
            companyId={companyId}
            onSegmentHeaderClick={openMatrixForSegment}
          />
        </div>
      ) : activeTab === 'matrix' ? (
        <div>{canViewMatrix ? <PermissionMatrixTab /> : <div>Access denied</div>}</div>
      ) : activeTab === 'debugger' ? (
        <div>
          <PermissionDebuggerTab userType={userType} warehouseId={warehouseId} companyId={companyId} />
        </div>
      ) : (
        <div>
          <AuditLogTab userType={userType} warehouseId={warehouseId} companyId={companyId} />
        </div>
      )}

      <EditUserModal
        open={open}
        setOpen={setOpen}
        currentUser={selectedUser}
        userType={userType}
        warehouseId={warehouseId}
        companyId={companyId}
        csrf={csrf}
        isImpersonating={isImpersonating}
      />
    </GlobalStyles.FullPageCard>
  )
}
